// BL-177 Step 2g (item 1): what the "live alerts" connection is doing, in one place, so a live test shows WHICH path delivered.
//
// An in-app alert reaches the page three ways: (1) 'changes' = Supabase Realtime row changes on public.notifications,
// (2) 'broadcast' = a private Realtime Broadcast nudge the database sends for the same row (migration bl177_step2g), and
// (3) the timed check ('check'), plus the push worker's message ('push'). 1 and 2 are the live paths. This module keeps their
// channel status and errors, which path delivered the last alert and how long after it was written, and notices a live path that
// says "connected" but did not deliver (an alert found by the timed check long after it was written).
import { useSyncExternalStore } from 'react';

export const LIVE_ALERTS_LOG_PREFIX = '[ia-live-alerts]';
/** An alert found by the timed check or the push message this many seconds after it was written means a live path should have delivered it. */
export const LIVE_ALERT_MISS_SECONDS = 6;

export const LIVE_ALERTS_LABELS = {
    connecting: 'Connecting',
    connected: 'Connected',
    reconnecting: 'Reconnecting',
    degraded: 'Connected, but alerts are not arriving live (using 30-second check)',
    off: 'Off (using 30-second check)',
};
export const SOURCE_LABELS = { changes: 'live update', broadcast: 'live nudge', push: 'push message', check: 'timed check', start: 'page load' };

const PATHS = ['changes', 'broadcast'];
const BAD = new Set(['CHANNEL_ERROR', 'TIMED_OUT', 'CLOSED']);

export function liveAlertsLabel(state) {
    return `Live alerts: ${LIVE_ALERTS_LABELS[state] || LIVE_ALERTS_LABELS.off}`;
}

export function createLiveAlertsMonitor({ log = console, now = () => Date.now() } = {}) {
    const listeners = new Set();
    let snapshot = null;
    const fresh = () => ({
        paths: { changes: { status: 'idle', error: '', everUp: false }, broadcast: { status: 'idle', error: '', everUp: false } },
        lastLive: null, // { path, at } the last time a live path delivered
        lastArrival: null, // { source, ageSeconds, at } the last alert that popped up
        lastMiss: null, // { ageSeconds, at }
        state: 'off',
    });
    let data = fresh();

    function derive() {
        const statuses = PATHS.map((path) => data.paths[path]);
        const up = statuses.some((entry) => entry.status === 'SUBSCRIBED');
        const trying = statuses.some((entry) => entry.status === 'connecting' || entry.status === 'JOINING');
        const bad = statuses.some((entry) => BAD.has(entry.status));
        const unavailable = statuses.every((entry) => entry.status === 'unavailable' || entry.status === 'idle');
        const missedAfterLive = data.lastMiss && (!data.lastLive || data.lastMiss.at > data.lastLive.at);
        if (up && missedAfterLive) return 'degraded';
        if (up) return 'connected';
        if (bad) return 'reconnecting';
        if (trying) return 'connecting';
        return unavailable ? 'off' : 'reconnecting';
    }
    function publish() {
        data = { ...data, state: derive() };
        snapshot = data;
        listeners.forEach((listener) => listener());
    }
    snapshot = data;

    return {
        getSnapshot: () => snapshot,
        subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
        /** status = SUBSCRIBED | CHANNEL_ERROR | TIMED_OUT | CLOSED | connecting | unavailable. error = the server's message, if any. */
        setPathStatus(path, status, error) {
            if (!PATHS.includes(path)) return;
            const message = error ? String(error.message || error) : '';
            const everUp = data.paths[path].everUp || status === 'SUBSCRIBED';
            data = { ...data, paths: { ...data.paths, [path]: { status, error: message, everUp } } };
            const line = `${LIVE_ALERTS_LOG_PREFIX} ${path} channel: ${status}${message ? ` (${message})` : ''}`;
            if (BAD.has(status) || message) log.warn?.(line); else log.info?.(line);
            publish();
        },
        /** A live path delivered an alert id. */
        recordLive(path, id) {
            data = { ...data, lastLive: { path, at: now(), id: id || null } };
            log.info?.(`${LIVE_ALERTS_LOG_PREFIX} ${path}: alert ${id || ''} arrived live`);
            publish();
        },
        /** A new alert reached the page and popped up; ageSeconds = how long after it was written. */
        recordArrival(source, ageSeconds) {
            const age = Number.isFinite(ageSeconds) ? Math.max(0, Math.round(ageSeconds * 10) / 10) : null;
            data = { ...data, lastArrival: { source, ageSeconds: age, at: now() } };
            log.info?.(`${LIVE_ALERTS_LOG_PREFIX} pop-up for an alert found by: ${source}${age != null ? `, ${age}s after it was written` : ''}`);
            if ((source === 'check' || source === 'push') && age != null && age > LIVE_ALERT_MISS_SECONDS && data.paths && PATHS.some((p) => data.paths[p].status === 'SUBSCRIBED')) {
                data = { ...data, lastMiss: { ageSeconds: age, at: now() } };
                log.warn?.(`${LIVE_ALERTS_LOG_PREFIX} a live path says SUBSCRIBED but did not deliver this alert (${age}s late): check the channel errors above, the Realtime policies on notifications and realtime.messages, and the user's token`);
            }
            publish();
        },
        /** True once a live path has delivered since the last miss: only then is the slow safety-net check trusted. */
        proven() {
            return Boolean(data.lastLive) && (!data.lastMiss || data.lastLive.at > data.lastMiss.at) && PATHS.some((p) => data.paths[p].status === 'SUBSCRIBED');
        },
        reset() { data = fresh(); publish(); },
    };
}

export const liveAlertsMonitor = createLiveAlertsMonitor();

export function useLiveAlertsStatus(monitor = liveAlertsMonitor) {
    return useSyncExternalStore(monitor.subscribe, monitor.getSnapshot, monitor.getSnapshot);
}

/** The line under the "Live alerts" status: which path delivered the last alert, in plain words. */
export function lastArrivalText(snapshot) {
    const arrival = snapshot && snapshot.lastArrival;
    if (!arrival) return '';
    const how = SOURCE_LABELS[arrival.source] || arrival.source;
    return `Last alert arrived by ${how}${arrival.ageSeconds != null ? `, ${arrival.ageSeconds} s after it was written` : ''}.`;
}
